import React from 'react';
import { useParams } from '@tanstack/react-router';

import { useAppSelector } from '@/store';
import { selectCurrentUserId } from '@/store/selectors/auth';
import { selectParticipants } from '@/store/selectors/sixDetail';
import { selectIsFileUploadModal } from '@/store/selectors/ui';
import SixDetailView from '@/pages/SixDetail/SixDetailView';
import { useFileDragAndDrop } from '@/hooks/sixDetail/useFileDragAndDrop';
import { useSixDetailPage } from '@/hooks/sixDetail/useSixDetailPage';
import type { UseSixDetailPageReturn } from '@/hooks/sixDetail/useSixDetailPage.types';
import type { DownloadFilterKey, FileEntry } from '@/types/SixDetail.types';
import { resolveCanDeleteSixFile, resolveCanDeleteSixFolder } from '@/utils/permissions/sixPermissions';

function SixDetailContainer(): React.ReactElement {
  const { sixId } = useParams({ from: '/app/sixes/$sixId' });
  const { state, handlers }: UseSixDetailPageReturn = useSixDetailPage(sixId);
  const isFileUploadModalOpen = useAppSelector(selectIsFileUploadModal);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const participants = useAppSelector(selectParticipants);
  const allFiles = useAppSelector((state) => state.sixDetail.files);

  const canDeleteFile = React.useCallback(
    (item: FileEntry): boolean => item.kind === 'file' && resolveCanDeleteSixFile(
      currentUserId,
      participants?.owners,
      item.uploadedByUserId,
    ),
    [currentUserId, participants?.owners],
  );

  const canShowRowActions = React.useCallback(
    (item: FileEntry): boolean => {
      if (item.kind === 'file') {
        return canDeleteFile(item);
      }

      return resolveCanDeleteSixFolder(currentUserId, participants?.owners, allFiles, item.path);
    },
    [allFiles, canDeleteFile, currentUserId, participants?.owners],
  );

  const dnd = useFileDragAndDrop({
    items: state.files,
    searchQuery: state.searchQuery,
    isMoving: false,
    onMoveItems: handlers.handleMoveItems,
  });

  const safeDownloadFilterKey: DownloadFilterKey =
    state.downloadFilterKey === 'downloaded' || state.downloadFilterKey === 'notDownloaded'
      ? state.downloadFilterKey
      : 'allFiles';

  return (
    <SixDetailView
      sixName={state.sixName}
      sixDescription={state.sixDescription}
      connectionStatus={state.connectionStatus}
      files={state.files}
      showPathColumn={state.showPathColumn}
      totalFilesCount={state.totalFilesCount}
      canManageAdministrativeActions={state.canManageAdministrativeActions}
      searchQuery={state.searchQuery}
      downloadFilterKey={safeDownloadFilterKey}
      selectedFileIds={state.selectedFileIds}
      sortKey={state.sortKey}
      sortDir={state.sortDir}
      membersCount={state.membersCount}
      lastActivityLabel={state.lastActivityLabel}
      participantCards={state.participantCards}
      activityCards={state.activityCards}
      breadcrumbItems={state.breadcrumbItems}
      inFlightUploads={state.inFlightUploads}
      showInlineUploads={!isFileUploadModalOpen}
      onNavigateDashboard={handlers.handleNavigateToDashboard}
      onSearchChange={handlers.handleSearchChange}
      onDownloadFilterChange={handlers.handleDownloadFilterChange}
      onSelectionChange={handlers.handleSelectionChange}
      onClearSelection={handlers.handleClearSelection}
      onSortChange={handlers.handleSortChange}
      onBulkDownload={handlers.handleBulkDownload}
      onBulkDelete={handlers.handleBulkDelete}
      onItemAction={handlers.handleItemAction}
      canShowRowActions={canShowRowActions}
      onUploadRetry={handlers.handleUploadRetry}
      onUploadProgressComplete={handlers.handleUploadProgressComplete}
      dndSensors={dnd.sensors}
      dndCollisionDetection={dnd.collisionDetection}
      dndActiveItem={dnd.activeItem}
      dndActiveDragCount={dnd.activeDragCount}
      isDragDisabled={dnd.isDragDisabled}
      onDragStart={dnd.handleDragStart}
      onDragEnd={dnd.handleDragEnd}
      onDragCancel={dnd.handleDragCancel}
      onManageAccessClick={handlers.handleManageAccessClick}
      onExportActivity={handlers.handleExportActivity}
    />
  );
}

export default SixDetailContainer;
